import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Input, Button, Typography, Space, Alert, Avatar, Divider, Spin, message, Select, Modal, Segmented,
} from 'antd';
import {
  CheckCircleOutlined, UserOutlined, IdcardOutlined, PhoneOutlined,
  QrcodeOutlined, CloseCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { apiClient } from '../../config/apiClient';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import winpadelLogo from '../../assets/winpadel.png';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es');

const { Title, Text } = Typography;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const ARG_TZ = 'America/Argentina/Buenos_Aires';
const MODAL_AUTO_CLOSE_MS = 20000;

const cardStyle = {
  background: 'rgba(45, 45, 45, 0.85)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: 20,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.35)',
  backdropFilter: 'blur(8px)',
};

const PageShell = ({ children, compact = false }) => (
  <div style={{
    minHeight: '100vh',
    background: 'radial-gradient(ellipse at top, #2a2a2a 0%, #141414 55%, #0d0d0d 100%)',
    padding: compact ? '20px 16px' : '32px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }}>
    {children}
  </div>
);

const HeaderLogo = ({ compact = false }) => (
  <div style={{ textAlign: 'center', marginBottom: compact ? 20 : 32 }}>
    <img
      src={winpadelLogo}
      alt="WinPadel"
      style={{
        maxWidth: compact ? 160 : 220,
        height: 'auto',
        marginBottom: 12,
        filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))',
      }}
    />
    <Text style={{
      display: 'block',
      color: '#9a9a9a',
      fontSize: compact ? 15 : 17,
      textTransform: 'capitalize',
      letterSpacing: 0.3,
    }}>
      {dayjs().tz(ARG_TZ).format('dddd D [de] MMMM YYYY')}
    </Text>
  </div>
);

const ResultadoExitosoProfesorContent = ({ resultado }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{
      width: 72,
      height: 72,
      borderRadius: '50%',
      background: 'rgba(82, 196, 26, 0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
    }}>
      <CheckCircleOutlined style={{ fontSize: 44, color: '#52c41a' }} />
    </div>
    <Title level={3} style={{ marginBottom: 4 }}>¡Ingreso registrado!</Title>
    <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
      {dayjs().tz(ARG_TZ).format('DD/MM/YYYY HH:mm')}
    </Text>

    <Space size="large" align="start" style={{ width: '100%', textAlign: 'left', justifyContent: 'center' }}>
      <Avatar size={64} icon={<TeamOutlined />} />
      <div>
        <Title level={4} style={{ margin: 0 }}>
          {resultado.profesor?.nombre} {resultado.profesor?.apellido}
        </Title>
        {resultado.profesor?.usuario && (
          <Text type="secondary">Usuario: {resultado.profesor.usuario}</Text>
        )}
      </div>
    </Space>

    <Divider />

    <Alert
      type="success"
      showIcon
      message="Ingreso registrado correctamente"
      description={
        resultado.fichadas_hoy > 1
          ? `Llevas ${resultado.fichadas_hoy} fichadas registradas hoy.`
          : 'Tu horario de ingreso quedó guardado.'
      }
    />
  </div>
);

const ResultadoExitosoContent = ({ resultado }) => {
  if (resultado?.tipoPersona === 'profesor') {
    return <ResultadoExitosoProfesorContent resultado={resultado} />;
  }

  return (
  <div style={{ textAlign: 'center' }}>
    <div style={{
      width: 72,
      height: 72,
      borderRadius: '50%',
      background: 'rgba(82, 196, 26, 0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
    }}>
      <CheckCircleOutlined style={{ fontSize: 44, color: '#52c41a' }} />
    </div>
    <Title level={3} style={{ marginBottom: 4 }}>¡Fichada exitosa!</Title>
    <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
      {dayjs().tz(ARG_TZ).format('DD/MM/YYYY HH:mm')}
    </Text>

    <Space size="large" align="start" style={{ width: '100%', textAlign: 'left' }}>
      <Avatar size={64} icon={<UserOutlined />} src={resultado.alumno?.foto} />
      <div>
        <Title level={4} style={{ margin: 0 }}>
          {resultado.alumno?.nombre} {resultado.alumno?.apellido}
        </Title>
        {resultado.alumno?.documento && (
          <Text type="secondary">DNI {resultado.alumno.documento}</Text>
        )}
        {resultado.alumno?.categoria && (
          <div><Text type="secondary">Categoría: {resultado.alumno.categoria}</Text></div>
        )}
      </div>
    </Space>

    <Divider />

    <Space direction="vertical" size={4} style={{ width: '100%', textAlign: 'left' }}>
      <Text strong>Clase de hoy</Text>
      <Text>
        {resultado.clase?.hora_desde?.substring(0, 5)} – {resultado.clase?.hora_hasta?.substring(0, 5)}
      </Text>
      {resultado.clase?.cancha && <Text type="secondary">{resultado.clase.cancha}</Text>}
      {resultado.clase?.profesor && <Text type="secondary">Prof. {resultado.clase.profesor}</Text>}
    </Space>

    <Divider />

    <Alert
      type={resultado.creditos?.saldo <= 0 ? 'warning' : 'success'}
      showIcon
      message={`Créditos restantes: ${resultado.creditos?.saldo}`}
      description={resultado.creditos?.mensaje_alerta || 'Se descontó 1 crédito por la clase.'}
    />
  </div>
  );
};

const FichadaModal = ({
  open,
  onClose,
  title,
  children,
  autoClose = true,
  footer = null,
  icon = null,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(MODAL_AUTO_CLOSE_MS / 1000);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || !autoClose) return undefined;

    setSecondsLeft(MODAL_AUTO_CLOSE_MS / 1000);
    const timer = setTimeout(() => onCloseRef.current(), MODAL_AUTO_CLOSE_MS);
    const interval = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [open, autoClose]);

  const defaultFooter = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      {autoClose ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          Se cierra en {secondsLeft}s
        </Text>
      ) : <span />}
      <Button type="primary" size="large" onClick={onClose}>
        Cerrar
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={(
        <Space>
          {icon}
          <span>{title}</span>
        </Space>
      )}
      footer={footer ?? defaultFooter}
      centered
      width={520}
      destroyOnClose
      maskClosable={false}
    >
      {children}
    </Modal>
  );
};

const TerminalFichada = ({
  modoPublico = false,
  qrTokenInicial = null,
  tipoPersonaInicial = 'jugador',
  profesorAutenticado = null,
}) => {
  const [tipoPersona, setTipoPersona] = useState(tipoPersonaInicial);
  const [telefono, setTelefono] = useState('');
  const [documento, setDocumento] = useState('');
  const [usuarioProfesor, setUsuarioProfesor] = useState(profesorAutenticado?.usuario || '');
  const [documentoNuevo, setDocumentoNuevo] = useState('');
  const [loading, setLoading] = useState(false);
  const [alumnoData, setAlumnoData] = useState(null);
  const [profesorData, setProfesorData] = useState(profesorAutenticado);
  const [fichadasHoy, setFichadasHoy] = useState(0);
  const [clases, setClases] = useState([]);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [qrToken, setQrToken] = useState(qrTokenInicial);
  const [qrLoading, setQrLoading] = useState(false);
  const [modal, setModal] = useState({ type: null, mensaje: '', turnoId: null });
  const [creditosInfo, setCreditosInfo] = useState(null);
  const qrRefreshTimer = useRef(null);
  const cargarQrRef = useRef(null);

  const programarRenovacionQr = useCallback((data) => {
    if (qrRefreshTimer.current) {
      clearTimeout(qrRefreshTimer.current);
      qrRefreshTimer.current = null;
    }
    if (!data?.expiresAt) return;

    const msHastaVencimiento = new Date(data.expiresAt).getTime() - Date.now();
    if (msHastaVencimiento <= 0) return;

    qrRefreshTimer.current = setTimeout(() => {
      cargarQrRef.current?.(true);
    }, msHastaVencimiento + 1500);
  }, []);

  const cargarQr = useCallback(async (silencioso = false) => {
    if (modoPublico) return;
    if (!silencioso) setQrLoading(true);
    try {
      const res = await apiClient.get('/fichada/qr-token');
      if (res.ok) {
        const data = await res.json();
        setQrData(data);
        setQrToken(data.token);
        programarRenovacionQr(data);
      }
    } catch {
      if (!silencioso) message.error('No se pudo cargar el código QR');
    } finally {
      if (!silencioso) setQrLoading(false);
    }
  }, [modoPublico, programarRenovacionQr]);

  cargarQrRef.current = cargarQr;

  useEffect(() => {
    if (!modoPublico) cargarQr();
    if (qrTokenInicial) validarToken(qrTokenInicial);
    return () => {
      if (qrRefreshTimer.current) clearTimeout(qrRefreshTimer.current);
    };
  }, [modoPublico, cargarQr, qrTokenInicial]);

  useEffect(() => {
    if (modoPublico || !qrData?.fecha) return undefined;
    const interval = setInterval(() => {
      const hoy = dayjs().tz(ARG_TZ).format('YYYY-MM-DD');
      if (qrData.fecha !== hoy) cargarQr(true);
    }, 60000);
    return () => clearInterval(interval);
  }, [modoPublico, qrData?.fecha, cargarQr]);

  const validarToken = async (token) => {
    try {
      const res = await fetch(`${API_BASE}/fichada/validar-token?t=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (!data.valido) {
        setModal({ type: 'error', mensaje: data.mensaje || 'Código QR inválido' });
      }
    } catch {
      setModal({ type: 'error', mensaje: 'Error al validar código QR' });
    }
  };

  const resetear = () => {
    setTelefono('');
    setDocumento('');
    setDocumentoNuevo('');
    setUsuarioProfesor(profesorAutenticado?.usuario || '');
    setAlumnoData(null);
    setProfesorData(profesorAutenticado);
    setFichadasHoy(0);
    setClases([]);
    setTurnoSeleccionado(null);
    setResultado(null);
    setCreditosInfo(null);
    setTipoPersona(tipoPersonaInicial);
    setModal({ type: null, mensaje: '', turnoId: null });
  };

  const cerrarModal = (reset = false) => {
    setModal({ type: null, mensaje: '', turnoId: null });
    if (reset) resetear();
  };

  const abrirModal = (type, extra = {}) => {
    setModal({ type, mensaje: '', turnoId: null, ...extra });
  };

  const formatearHorarioClase = (clase) => clase?.tur_hora_desde?.substring(0, 5) || '';

  const mensajeYaFichado = (clasesList) => {
    const fichadas = (clasesList || []).filter((c) => c.ya_fichado);
    const horarios = fichadas.map(formatearHorarioClase).filter(Boolean).join(', ');
    if (fichadas.length === clasesList?.length) {
      return horarios
        ? `Ya fichaste todas tus clases de hoy (${horarios}).`
        : 'Ya fichaste todas tus clases de hoy.';
    }
    return horarios
      ? `Ya fichaste la clase de las ${horarios}.`
      : 'Ya realizaste la fichada para esta clase.';
  };

  const solicitarConfirmacionFichada = (alumnoId, turnoId) => {
    const clase = clases.find((c) => c.tur_id === turnoId);
    if (clase?.asiste_final === false) {
      abrirModal('error', {
        mensaje: `No podés fichar: marcaste que no asistís a la clase de las ${formatearHorarioClase(clase)}.`,
      });
      return;
    }
    abrirModal('confirmar_fichada', { turnoId, clase });
  };

  const confirmarYFichar = async () => {
    const turnoId = modal.turnoId;
    cerrarModal(false);
    if (turnoId && alumnoData?.id) {
      await registrarFichada(alumnoData.id, turnoId);
    }
  };

  const procesarClases = async (alumno, clasesList) => {
    if (!clasesList?.length) {
      abrirModal('sin_clase');
      return;
    }

    const pendientes = clasesList.filter((c) => !c.ya_fichado);
    if (!pendientes.length) {
      abrirModal('ya_fichado', { mensaje: mensajeYaFichado(clasesList) });
      return;
    }

    if (pendientes.length === 1) {
      solicitarConfirmacionFichada(alumno.id, pendientes[0].tur_id);
    } else {
      setTurnoSeleccionado(null);
      abrirModal('seleccionar_clase');
    }
  };

  const confirmarYFicharProfesor = async () => {
    const profId = profesorData?.id;
    cerrarModal(false);
    if (profId) await registrarFichadaProfesor(profId);
  };

  const identificar = async () => {
    if (tipoPersona === 'profesor') {
      if (!usuarioProfesor.trim() && !telefono.trim() && !documento.trim()) {
        abrirModal('error', { mensaje: 'Ingrese usuario, teléfono o documento' });
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/fichada/identificar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipoPersona: 'profesor',
            usuario: usuarioProfesor.trim(),
            telefono: telefono.trim(),
            documento: documento.trim(),
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          abrirModal('error', { mensaje: data.message || 'Profesor no encontrado' });
          return;
        }
        setProfesorData(data.profesor);
        setFichadasHoy(data.fichadas_hoy || 0);
        abrirModal('confirmar_profesor');
      } catch {
        abrirModal('error', { mensaje: 'Error al identificar profesor' });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!telefono.trim() && !documento.trim()) {
      abrirModal('error', { mensaje: 'Ingrese teléfono o documento' });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/fichada/identificar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telefono: telefono.trim(), documento: documento.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        abrirModal('error', { mensaje: data.message || 'Jugador no encontrado' });
        return;
      }

      setAlumnoData(data.alumno);
      setClases(data.clases || []);
      setCreditosInfo(data.creditos || null);

      if (data.requiere_documento) {
        setDocumentoNuevo('');
        abrirModal('documento');
        return;
      }

      await procesarClases(data.alumno, data.clases);
    } catch {
      abrirModal('error', { mensaje: 'Error al identificar jugador' });
    } finally {
      setLoading(false);
    }
  };

  const guardarDocumentoYContinuar = async () => {
    if (!documentoNuevo.trim()) {
      abrirModal('error', { mensaje: 'Ingrese el número de documento' });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/fichada/documento`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alumnoId: alumnoData.id, documento: documentoNuevo.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        abrirModal('error', { mensaje: data.message || 'Error al guardar documento' });
        return;
      }

      const clasesRes = await fetch(`${API_BASE}/fichada/identificar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documento: documentoNuevo.trim() }),
      });
      const clasesData = await clasesRes.json();
      setClases(clasesData.clases || []);
      setCreditosInfo(clasesData.creditos || null);
      cerrarModal(false);
      await procesarClases(alumnoData, clasesData.clases);
    } catch {
      abrirModal('error', { mensaje: 'Error al guardar documento' });
    } finally {
      setLoading(false);
    }
  };

  const registrarFichadaProfesor = async (profesorId) => {
    setLoading(true);
    try {
      let endpoint = '/fichada/registrar-profesor-publico';
      const headers = { 'Content-Type': 'application/json' };

      if (!modoPublico) {
        const token = localStorage.getItem('user');
        const userToken = token ? JSON.parse(token).token : null;
        if (userToken) {
          endpoint = '/fichada/registrar-profesor';
          headers.Authorization = `Bearer ${userToken}`;
        }
      }

      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ profesorId, qrToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        abrirModal('error', { mensaje: data.message || 'Error al registrar ingreso' });
        return;
      }
      setResultado(data);
      abrirModal('exito');
    } catch {
      abrirModal('error', { mensaje: 'Error al registrar ingreso' });
    } finally {
      setLoading(false);
    }
  };

  const registrarFichada = async (alumnoId, turnoId) => {
    setLoading(true);
    try {
      let endpoint = '/fichada/registrar-publico';
      const headers = { 'Content-Type': 'application/json' };

      if (!modoPublico) {
        const token = localStorage.getItem('user');
        const userToken = token ? JSON.parse(token).token : null;
        if (userToken) {
          endpoint = '/fichada/registrar';
          headers.Authorization = `Bearer ${userToken}`;
        }
      }

      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ alumnoId, turnoId, qrToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        abrirModal('error', { mensaje: data.message || 'Error al fichar' });
        return;
      }
      setResultado(data);
      abrirModal('exito');
    } catch {
      abrirModal('error', { mensaje: 'Error al registrar fichada' });
    } finally {
      setLoading(false);
    }
  };

  const modalAbierto = Boolean(modal.type);

  const renderModalContent = () => {
    switch (modal.type) {
      case 'documento':
        return (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              type="info"
              showIcon
              message={`Hola ${alumnoData?.nombre}!`}
              description="Necesitamos tu número de documento para completar la fichada."
            />
            <Input
              size="large"
              prefix={<IdcardOutlined />}
              placeholder="Número de documento (DNI)"
              value={documentoNuevo}
              onChange={(e) => setDocumentoNuevo(e.target.value.replace(/\D/g, ''))}
              maxLength={11}
              autoFocus
            />
            <Button type="primary" size="large" block loading={loading} onClick={guardarDocumentoYContinuar}>
              Continuar
            </Button>
          </Space>
        );

      case 'seleccionar_clase':
        return (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Text>Seleccioná la clase a fichar:</Text>
            <Select
              size="large"
              style={{ width: '100%' }}
              placeholder="Elegir clase"
              value={turnoSeleccionado}
              onChange={setTurnoSeleccionado}
              options={clases.filter((c) => !c.ya_fichado).map((c) => ({
                value: c.tur_id,
                label: `${c.tur_hora_desde?.substring(0, 5)} - ${c.cancha_descripcion || 'Cancha'} (${c.categoria_descripcion || ''})`,
              }))}
            />
            <Button
              type="primary"
              size="large"
              block
              loading={loading}
              disabled={!turnoSeleccionado}
              onClick={() => solicitarConfirmacionFichada(alumnoData.id, turnoSeleccionado)}
            >
              Fichar
            </Button>
          </Space>
        );

      case 'sin_clase':
        return (
          <Alert
            type="warning"
            showIcon
            message="Sin clase hoy"
            description={`${alumnoData?.nombre || ''} ${alumnoData?.apellido || ''} no tiene ninguna reserva asignada para hoy.`.trim()}
          />
        );

      case 'ya_fichado':
        return (
          <Alert
            type="info"
            showIcon
            message="Ya fichado"
            description={modal.mensaje || 'Este jugador ya realizó la fichada para su clase de hoy.'}
          />
        );

      case 'confirmar_profesor':
        return (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              type="info"
              showIcon
              message={`Hola ${profesorData?.nombre}!`}
              description={
                fichadasHoy > 0
                  ? `Ya registraste ${fichadasHoy} ingreso${fichadasHoy === 1 ? '' : 's'} hoy. ¿Querés fichar nuevamente?`
                  : 'Confirmá para registrar tu ingreso al club.'
              }
            />
            <Button type="primary" size="large" block loading={loading} onClick={confirmarYFicharProfesor}>
              Registrar ingreso
            </Button>
            <Button block onClick={() => cerrarModal(true)}>Cancelar</Button>
          </Space>
        );

      case 'confirmar_fichada': {
        const saldo = creditosInfo?.saldo ?? '?';
        const hora = formatearHorarioClase(modal.clase);
        return (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              type={creditosInfo?.alerta ? 'warning' : 'info'}
              showIcon
              message={`Tenés ${saldo} crédito${saldo === 1 ? '' : 's'}`}
              description={
                hora
                  ? `Se descontará 1 crédito por la clase de las ${hora}.`
                  : 'Se descontará 1 crédito por esta clase.'
              }
            />
            {creditosInfo?.alerta && (
              <Text type="warning">
                Estás con pocos créditos. Recordá comprar antes de la próxima clase.
              </Text>
            )}
            <Button type="primary" size="large" block loading={loading} onClick={confirmarYFichar}>
              Confirmar fichada
            </Button>
            <Button block onClick={() => cerrarModal(true)}>Cancelar</Button>
          </Space>
        );
      }

      case 'exito':
        return resultado ? <ResultadoExitosoContent resultado={resultado} /> : null;

      case 'error':
        return (
          <Alert
            type="error"
            showIcon
            message="No se pudo completar"
            description={modal.mensaje || 'Ocurrió un error inesperado.'}
          />
        );

      default:
        return null;
    }
  };

  const modalConfig = {
    documento: {
      title: 'Completar datos',
      icon: <IdcardOutlined style={{ color: '#1677ff' }} />,
      autoClose: false,
      onClose: () => cerrarModal(true),
    },
    seleccionar_clase: {
      title: 'Elegir clase',
      icon: <InfoCircleOutlined style={{ color: '#1677ff' }} />,
      autoClose: false,
      onClose: () => cerrarModal(true),
    },
    confirmar_profesor: {
      title: 'Registrar ingreso',
      icon: <TeamOutlined style={{ color: '#1677ff' }} />,
      autoClose: false,
      onClose: () => cerrarModal(true),
    },
    confirmar_fichada: {
      title: 'Confirmar fichada',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      autoClose: false,
      onClose: () => cerrarModal(true),
    },
    sin_clase: {
      title: 'Sin clase',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      autoClose: true,
      onClose: () => cerrarModal(true),
    },
    ya_fichado: {
      title: 'Ya fichado',
      icon: <InfoCircleOutlined style={{ color: '#1677ff' }} />,
      autoClose: true,
      onClose: () => cerrarModal(true),
    },
    exito: {
      title: 'Fichada registrada',
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      autoClose: true,
      onClose: () => cerrarModal(true),
    },
    error: {
      title: 'Atención',
      icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      autoClose: true,
      onClose: () => cerrarModal(false),
    },
  };

  const config = modal.type ? modalConfig[modal.type] : null;

  return (
    <PageShell compact={modoPublico}>
      <div style={{ width: '100%', maxWidth: 960 }}>
        <HeaderLogo compact={modoPublico} />

        <div style={{
          display: 'grid',
          gridTemplateColumns: !modoPublico ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr',
          gap: 24,
          alignItems: 'stretch',
        }}>
          {!modoPublico && (
            <div style={{ ...cardStyle, padding: '28px 24px', textAlign: 'center' }}>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                  <QrcodeOutlined style={{ fontSize: 20, color: '#aaa', marginRight: 8 }} />
                  <Text style={{ color: '#e0e0e0', fontSize: 17, fontWeight: 500 }}>
                    Escaneá con tu celular
                  </Text>
                </div>

                <div style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: 20,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                  minHeight: 260,
                  minWidth: 260,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {qrLoading ? (
                    <Spin size="large" />
                  ) : qrData?.url ? (
                    <QRCodeSVG value={qrData.url} size={220} level="M" />
                  ) : (
                    <Text type="secondary">QR no disponible</Text>
                  )}
                </div>

                <Text type="secondary" style={{ fontSize: 13 }}>
                  Apuntá la cámara de tu teléfono al código
                </Text>
              </Space>
            </div>
          )}

          <div style={{ ...cardStyle, padding: '28px 32px' }}>
            <Title level={4} style={{ color: '#fff', marginTop: 0, marginBottom: 16, fontWeight: 500 }}>
              Ingresá tus datos
            </Title>

            {!profesorAutenticado && (
              <Segmented
                block
                size="large"
                value={tipoPersona}
                onChange={setTipoPersona}
                options={[
                  { label: 'Jugador', value: 'jugador', icon: <UserOutlined /> },
                  { label: 'Profesor', value: 'profesor', icon: <TeamOutlined /> },
                ]}
                style={{ marginBottom: 20 }}
              />
            )}

            {profesorAutenticado ? (
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Alert
                  type="info"
                  showIcon
                  message={`Hola ${profesorAutenticado.nombre}!`}
                  description="Registrá tu ingreso al club con un solo clic."
                />
                <Button
                  type="primary"
                  size="large"
                  block
                  loading={loading}
                  onClick={() => {
                    setProfesorData(profesorAutenticado);
                    setFichadasHoy(0);
                    abrirModal('confirmar_profesor');
                  }}
                  style={{ height: 56, fontSize: 18, fontWeight: 600, borderRadius: 10 }}
                >
                  Registrar mi ingreso
                </Button>
              </Space>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {tipoPersona === 'profesor' && (
                  <>
                    <Input
                      size="large"
                      prefix={<UserOutlined style={{ color: '#888' }} />}
                      placeholder="Usuario de acceso"
                      value={usuarioProfesor}
                      onChange={(e) => setUsuarioProfesor(e.target.value)}
                      style={{ fontSize: 18, height: 56, borderRadius: 10 }}
                    />
                    <Divider plain style={{ margin: '4px 0', color: '#666', borderColor: 'rgba(255,255,255,0.06)' }}>
                      o
                    </Divider>
                  </>
                )}
                <Input
                  size="large"
                  prefix={<PhoneOutlined style={{ color: '#888' }} />}
                  placeholder="Teléfono (sin 0 ni 15)"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  style={{ fontSize: 18, height: 56, borderRadius: 10 }}
                />
                <Divider plain style={{ margin: '4px 0', color: '#666', borderColor: 'rgba(255,255,255,0.06)' }}>
                  o
                </Divider>
                <Input
                  size="large"
                  prefix={<IdcardOutlined style={{ color: '#888' }} />}
                  placeholder="Número de documento"
                  value={documento}
                  onChange={(e) => setDocumento(e.target.value.replace(/\D/g, ''))}
                  maxLength={11}
                  style={{ fontSize: 18, height: 56, borderRadius: 10 }}
                />
                <Button
                  type="primary"
                  size="large"
                  block
                  loading={loading}
                  onClick={identificar}
                  style={{ height: 56, fontSize: 18, fontWeight: 600, borderRadius: 10, marginTop: 8 }}
                >
                  {tipoPersona === 'profesor' ? 'Registrar ingreso' : 'Fichar'}
                </Button>
              </Space>
            )}
          </div>
        </div>
      </div>

      {config && (
        <FichadaModal
          open={modalAbierto}
          onClose={config.onClose}
          title={config.title}
          icon={config.icon}
          autoClose={config.autoClose}
        >
          {renderModalContent()}
        </FichadaModal>
      )}
    </PageShell>
  );
};

export default TerminalFichada;
export { ResultadoExitosoContent as ResultadoExitoso };
